class LyMainProductMediaPreviewerBase extends LyFullScrollCarousel {
    #mediaScope = null
    #activePointerId = null
    #pendingSyncRequest = null
    #pendingPublishedIndex = null
    #settleTimer = null
    #isSyncingFromBus = false

    connectedCallback() {
        super.connectedCallback()

        this.#mediaScope = this.closest('.ly-main-product-media-wrapper')

        this.thumbnailIndexChangeUnsubscriber = window.subscribe(
            'bus:main_product_media_thumbnailer_index_changed',
            this.#handleThumbnailIndexChanged
        )

        this._scroller.addEventListener('pointerdown', this.#handlePointerDown, { passive: true })
        this._scroller.addEventListener('scroll', this.#handleScroll, { passive: true })
        this._scroller.addEventListener('scrollend', this.#handleScrollEnd)
        window.addEventListener('pointerup', this.#handlePointerEnd)
        window.addEventListener('pointercancel', this.#handlePointerEnd)

        this._handleMediaIndexChanged(this._index)
    }

    disconnectedCallback() {
        this.thumbnailIndexChangeUnsubscriber?.()

        this._scroller?.removeEventListener('pointerdown', this.#handlePointerDown)
        this._scroller?.removeEventListener('scroll', this.#handleScroll)
        this._scroller?.removeEventListener('scrollend', this.#handleScrollEnd)
        window.removeEventListener('pointerup', this.#handlePointerEnd)
        window.removeEventListener('pointercancel', this.#handlePointerEnd)

        clearTimeout(this.#settleTimer)
        this.#settleTimer = null
        this.#activePointerId = null
        this.#pendingSyncRequest = null
        this.#pendingPublishedIndex = null
        this.#mediaScope = null

        super.disconnectedCallback()
    }

    _createSyncRequest(thumbnailIndex) {
        return {
            behavior: 'smooth',
            previewIndex: thumbnailIndex
        }
    }

    _afterSyncRequest() { }

    _mapPreviewIndex(index) {
        return index
    }

    _canPublishIndex() {
        return true
    }

    _handleMediaIndexChanged() { }

    _clearPendingMediaChange = () => {
        clearTimeout(this.#settleTimer)
        this.#settleTimer = null
        this.#pendingSyncRequest = null
        this.#pendingPublishedIndex = null
    }

    _publishMediaIndex = (index) => {
        window.publish('bus:main_product_media_previewer_index_changed', {
            index,
            scope: this.#mediaScope
        })
    }

    _settleToIndexInstantly = (index) => {
        const previousScrollBehavior = this._scroller.style.scrollBehavior

        this._scroller.style.scrollBehavior = 'auto'

        try {
            this._settleToIndex(index, { behavior: 'auto' })
        } finally {
            this._scroller.style.scrollBehavior = previousScrollBehavior
        }
    }

    _onIndexChanged = (index) => {
        this._handleMediaIndexChanged(index)

        if (this.#isSyncingFromBus || !this._canPublishIndex()) return

        this.#pendingPublishedIndex = this._mapPreviewIndex(index)
        this.#scheduleSettle()
    }

    _onScrollSettled = () => {
        this.#settle()
    }

    #isInteracting = () => {
        return this._isDragging || this.#activePointerId !== null
    }

    #handleThumbnailIndexChanged = (payload) => {
        const value = typeof payload === 'object' ? payload?.index : payload
        const scope = typeof payload === 'object' ? payload?.scope : null

        if (scope && scope !== this.#mediaScope) return

        const index = Number(value)
        if (!Number.isFinite(index)) return

        const request = this._createSyncRequest(Math.trunc(index))

        // null 表示切换到了不属于此滚动容器的媒体，例如独立的 3D 模型。
        if (!request) {
            this.#pendingSyncRequest = null
            this.#pendingPublishedIndex = null
            return
        }

        // 多指或交叉操作时保留最后一次明确选择，等当前手势稳定后再执行。
        if (this.#isInteracting()) {
            this.#pendingSyncRequest = request
            this.#pendingPublishedIndex = null
            return
        }

        this.#applySyncRequest(request)
    }

    #applySyncRequest = (request) => {
        this.#pendingPublishedIndex = null

        if (request.previewIndex !== this._index) {
            this.#isSyncingFromBus = true

            if (request.behavior === 'instant') {
                this._settleToIndexInstantly(request.previewIndex)
            } else {
                this._settleToIndex(request.previewIndex, { behavior: request.behavior })
            }

            this.#isSyncingFromBus = false
        }

        this._afterSyncRequest(request)
    }

    #handlePointerDown = (event) => {
        if (this.#activePointerId !== null) return

        this.#activePointerId = event.pointerId
        clearTimeout(this.#settleTimer)
        this.#settleTimer = null
    }

    #handlePointerEnd = (event) => {
        if (event.pointerId !== this.#activePointerId) return

        this.#activePointerId = null
        this.#scheduleSettle()
    }

    #handleScroll = () => {
        if (this.#pendingPublishedIndex === null && !this.#pendingSyncRequest) return
        this.#scheduleSettle()
    }

    #handleScrollEnd = () => {
        this.#settle()
    }

    #scheduleSettle = () => {
        clearTimeout(this.#settleTimer)
        this.#settleTimer = setTimeout(this.#settle, 140)
    }

    #settle = () => {
        clearTimeout(this.#settleTimer)
        this.#settleTimer = null

        if (this.#isInteracting()) return

        if (this.#pendingSyncRequest) {
            const request = this.#pendingSyncRequest
            this.#pendingSyncRequest = null
            this.#applySyncRequest(request)
            return
        }

        if (this.#pendingPublishedIndex === null || !this._canPublishIndex()) return

        const index = this.#pendingPublishedIndex
        this.#pendingPublishedIndex = null
        this._publishMediaIndex(index)
    }
}

if (!customElements.get('ly-main-product-media-previewer')) {
    customElements.define('ly-main-product-media-previewer', class extends LyMainProductMediaPreviewerBase {
        _handleMediaIndexChanged = () => {
            const firstItem = this._items[0]
            const shouldDisableDrag = this._index === 0 && firstItem?.classList.contains('model')

            this.toggleAttribute('disable-drag', shouldDisableDrag)
        }
    })
}

if (!customElements.get('ly-main-product-media-previewer-with-model')) {
    customElements.define('ly-main-product-media-previewer-with-model', class extends LyMainProductMediaPreviewerBase {

        connectedCallback() {
            super.connectedCallback()

            this.closeBtn = this.querySelector('.ly-main-product-media-previewer-with-model-close-btn')
            this.closeBtn?.addEventListener('click', this.closePreview)

            this.previewCloseRequestUnsubscriber = window.subscribe(
                'bus:main_product_media_preview_close_requested',
                this.handlePreviewCloseRequest
            )
        }

        disconnectedCallback() {
            this.closeBtn?.removeEventListener('click', this.closePreview)
            this.closeBtn = null
            this.previewCloseRequestUnsubscriber?.()

            super.disconnectedCallback()
        }

        handlePreviewCloseRequest = (payload) => {
            if (!this.classList.contains('is-previewing')) return

            const mediaScope = this.closest('.ly-main-product-media-wrapper')
            if (payload?.scope && payload.scope !== mediaScope) return

            this.closePreview()
        }

        closePreview = () => {
            this._clearPendingMediaChange()
            this.classList.remove('is-previewing')

            if (this._index !== 0) {
                this._settleToIndexInstantly(0)
            }

            this._publishMediaIndex(0)
        }

        _createSyncRequest(thumbnailIndex) {
            if (thumbnailIndex <= 0) {
                this.classList.remove('is-previewing')
                return null
            }

            const shouldReveal = !this.classList.contains('is-previewing')

            return {
                // 缩略图第 0 项是模型，滚动预览内部不包含模型。
                previewIndex: thumbnailIndex - 1,
                // 先瞬间定位内部媒体，再执行遮罩进入动画，避免两层横向动画叠加。
                behavior: shouldReveal ? 'instant' : 'smooth',
                shouldReveal
            }
        }

        _afterSyncRequest = (request) => {
            if (request.shouldReveal) {
                this.classList.add('is-previewing')
            }
        }

        _mapPreviewIndex(index) {
            return index + 1
        }

        _canPublishIndex() {
            return this.classList.contains('is-previewing')
        }
    })
}
